import React, { useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  DocumentIcon,
  EllipsisVerticalIcon,
  PhoneIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import './WhatsAppDevicePreview.css';
import type { WhatsAppTemplateButton } from '../../models/MasterAdmin/whatsappCampaign';
import { isCtaTemplateButton } from './whatsappTemplatePreview';
import { renderWhatsAppFormattedText } from './whatsappTextFormatting';

export type DevicePlatform = 'android' | 'ios';

type WhatsAppDevicePreviewProps = {
  message: string;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerText?: string | null;
  footer?: string | null;
  buttons?: WhatsAppTemplateButton[];
  contactName?: string;
  className?: string;
};

const DEVICE_CONFIG = {
  android: {
    width: 360,
    height: 740,
  },
  ios: {
    width: 375,
    height: 812,
  },
};

const PREVIEW_SCALE = 0.72;
const DUAL_PREVIEW_SCALE = 0.46;

function DeviceFrame({
  platform,
  scale,
  message,
  headerMediaUrl,
  headerMimeType,
  headerText,
  footer,
  buttons,
  contactName,
}: {
  platform: DevicePlatform;
  scale: number;
  message: string;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerText?: string | null;
  footer?: string | null;
  buttons?: WhatsAppTemplateButton[];
  contactName: string;
}) {
  const config = DEVICE_CONFIG[platform];
  const scaledW = Math.round(config.width * scale);
  const scaledH = Math.round(config.height * scale);

  return (
    <div className="wa-device-preview__frame-outer">
      <div className="wa-device-preview__frame-host" style={{ width: scaledW, height: scaledH }}>
        <div
          className={`wa-device-preview__frame-inner wa-device-preview__frame-inner--${platform}`}
          style={{
            width: config.width,
            height: config.height,
            transform: `scale(${scale})`,
          }}
        >
          {/* Custom phone chassis rendering */}
          <div className={`relative w-full h-full border-[10px] border-slate-800 bg-slate-900 overflow-hidden shadow-2xl flex flex-col ${
            platform === 'ios' ? 'rounded-[44px]' : 'rounded-3xl'
          }`}>
            {/* Notch / Camera details */}
            {platform === 'ios' && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-36 h-[26px] bg-slate-800 rounded-b-2xl z-50 flex items-center justify-center gap-2">
                <div className="w-10 h-1 bg-black rounded-full opacity-40" />
                <div className="w-2.5 h-2.5 bg-black rounded-full" />
              </div>
            )}
            {platform === 'android' && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-black rounded-full z-50 border border-slate-700" />
            )}
            
            {/* Screen Content */}
            <div className="flex-1 w-full min-h-0 relative bg-[#e5ddd5]">
              <WhatsAppChatScreen
                message={message}
                headerMediaUrl={headerMediaUrl}
                headerMimeType={headerMimeType}
                headerText={headerText}
                footer={footer}
                buttons={buttons}
                contactName={contactName}
                platform={platform}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateButtonIcon({ type }: { type: string }) {
  const t = type.toUpperCase();
  if (t === 'PHONE_NUMBER') return <PhoneIcon className="h-4 w-4 shrink-0" />;
  if (t === 'URL' || t === 'CALL_TO_ACTION') return <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />;
  return null;
}

function MessageBubble({
  message,
  headerMediaUrl,
  headerMimeType,
  headerText,
  footer,
  buttons = [],
}: {
  message: string;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerText?: string | null;
  footer?: string | null;
  buttons?: WhatsAppTemplateButton[];
}) {
  const isImage = headerMimeType?.startsWith('image/') || (!headerMimeType && headerMediaUrl);
  const isVideo = headerMimeType?.startsWith('video/');
  const isDocument = headerMediaUrl && !isImage && !isVideo;
  const text = message || 'Your template message preview…';
  const ctaButtons = buttons.filter(isCtaTemplateButton);
  const quickReplyButtons = buttons.filter((b) => !isCtaTemplateButton(b));
  const bodyLines = text.split('\n');

  return (
    <div className="relative max-w-[92%]">
      <div
        className="absolute -left-1.5 top-0 h-3 w-3 bg-white"
        style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
        aria-hidden
      />
      <div className="overflow-hidden rounded-lg rounded-tl-none bg-white shadow-md ring-1 ring-black/5">
        {headerMediaUrl && isImage && (
          <img src={headerMediaUrl} alt="" className="max-h-32 w-full object-cover" />
        )}
        {headerMediaUrl && isVideo && (
          <video
            src={headerMediaUrl}
            controls
            className="max-h-44 w-full rounded-t-lg object-cover"
          />
        )}
        {isDocument && (
          <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-700">
            <DocumentIcon className="h-4 w-4 shrink-0 text-red-500" />
            <span className="truncate">Document header</span>
          </div>
        )}
        {headerText && !headerMediaUrl && (
          <div className="border-b border-gray-100 px-3 py-2.5 text-[15px] font-semibold leading-snug text-gray-900">
            {headerText}
          </div>
        )}
        <div className="whitespace-pre-wrap break-words px-3 py-2.5 text-[14px] leading-snug text-gray-900">
          {bodyLines.map((line, index) => (
            <React.Fragment key={index}>
              {index > 0 ? <br /> : null}
              {renderWhatsAppFormattedText(line)}
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-end justify-between gap-2 px-3 pb-2">
          {footer ? <span className="text-[11px] text-gray-400">{footer}</span> : <span />}
          <span className="shrink-0 text-[11px] text-gray-400">12:30 PM</span>
        </div>
        {ctaButtons.map((btn, i) => (
          <div key={`cta-${i}`} className="border-t border-gray-200">
            <div className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-[13px] font-medium text-[#009de2]">
              <TemplateButtonIcon type={btn.type} />
              <span className="truncate">{btn.text}</span>
            </div>
          </div>
        ))}
      </div>
      {quickReplyButtons.length > 0 && (
        <div className="mt-1 space-y-1">
          {quickReplyButtons.map((btn, i) => (
            <div
              key={`qr-${i}`}
              className="rounded-lg bg-white px-3 py-2 text-center text-[13px] font-medium text-[#009de2] shadow-sm ring-1 ring-black/5"
            >
              {btn.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WhatsAppChatScreen({
  message,
  headerMediaUrl,
  headerMimeType,
  headerText,
  footer,
  buttons,
  contactName,
  platform,
}: {
  message: string;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerText?: string | null;
  footer?: string | null;
  buttons?: WhatsAppTemplateButton[];
  contactName: string;
  platform: DevicePlatform;
}) {
  const isAndroid = platform === 'android';
  const headerBg = isAndroid ? 'bg-[#075e54]' : 'bg-[#f6f6f6]';
  const headerTextColor = isAndroid ? 'text-white' : 'text-[#075e54]';
  const iconColor = isAndroid ? 'text-white/90' : 'text-[#075e54]';
  const subText = isAndroid ? 'text-white/80' : 'text-gray-500';

  return (
    <div className={`wa-chat-screen wa-chat-screen--${platform} flex h-full w-full flex-col overflow-hidden bg-[#e5ddd5]`}>
      {platform === 'ios' && <div className="wa-chat-screen__ios-notch-spacer shrink-0 bg-[#f6f6f6]" aria-hidden />}

      <div className={`flex shrink-0 items-center gap-2 px-3 py-2.5 ${headerBg} ${headerTextColor}`}>
        <ChevronLeftIcon className={`h-5 w-5 shrink-0 ${iconColor}`} />
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-sm font-bold text-white">
          V
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight">{contactName}</p>
          <p className={`truncate text-[11px] ${subText}`}>Business account</p>
        </div>
        <EllipsisVerticalIcon className={`h-5 w-5 shrink-0 ${iconColor}`} />
      </div>

      <div
        className="wa-chat-body flex min-h-0 flex-1 flex-col items-start overflow-y-auto overflow-x-hidden px-3 pb-3 pt-2"
        style={{
          backgroundColor: '#e5ddd5',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8c4bc' fill-opacity='0.18'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <div className="mb-2.5 flex w-full justify-center">
          <span className="rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 shadow-sm">
            TODAY
          </span>
        </div>
        <MessageBubble
          message={message}
          headerMediaUrl={headerMediaUrl}
          headerMimeType={headerMimeType}
          headerText={headerText}
          footer={footer}
          buttons={buttons}
        />
      </div>
    </div>
  );
}

const WhatsAppDevicePreview: React.FC<WhatsAppDevicePreviewProps> = ({
  message,
  headerMediaUrl,
  headerMimeType,
  headerText,
  footer,
  buttons = [],
  contactName = 'Vedika.Health',
  className = '',
}) => {
  const [platform, setPlatform] = useState<DevicePlatform>('android');

  return (
    <div className={`wa-device-preview ${className}`}>
      <div className="wa-device-preview__header">
        <p className="wa-device-preview__label">Preview</p>
        <div className="wa-device-preview__toggle">
          <button
            type="button"
            onClick={() => setPlatform('android')}
            aria-pressed={platform === 'android'}
            className={platform === 'android' ? 'is-active' : ''}
          >
            Android
          </button>
          <button
            type="button"
            onClick={() => setPlatform('ios')}
            aria-pressed={platform === 'ios'}
            className={platform === 'ios' ? 'is-active' : ''}
          >
            iOS
          </button>
        </div>
      </div>

      <DeviceFrame
        platform={platform}
        scale={PREVIEW_SCALE}
        message={message}
        headerMediaUrl={headerMediaUrl}
        headerMimeType={headerMimeType}
        headerText={headerText}
        footer={footer}
        buttons={buttons}
        contactName={contactName}
      />
    </div>
  );
};

export function WhatsAppDualDevicePreview({
  message,
  headerMediaUrl,
  headerMimeType,
  headerText,
  footer,
  buttons = [],
  contactName = 'Vedika.Health',
  className = '',
}: WhatsAppDevicePreviewProps) {
  return (
    <div className={`wa-device-preview wa-device-preview--dual ${className}`}>
      {(['android', 'ios'] as const).map((platform) => (
        <div key={platform} className="wa-device-preview__unit">
          <p className="wa-device-preview__label">{platform === 'android' ? 'Android' : 'iOS'}</p>
          <DeviceFrame
            platform={platform}
            scale={DUAL_PREVIEW_SCALE}
            message={message}
            headerMediaUrl={headerMediaUrl}
            headerMimeType={headerMimeType}
            headerText={headerText}
            footer={footer}
            buttons={buttons}
            contactName={contactName}
          />
        </div>
      ))}
    </div>
  );
}

export default WhatsAppDevicePreview;
